import { Link } from 'react-router-dom';
import {
  UXP_PA_ID,
  COMMAND_CENTER_METRICS,
  ORG_MANAGEMENT_FEATURES,
  USER_ROLE_FEATURES,
  PERMISSION_SCOPES,
  AI_GOVERNANCE_FEATURES,
  PROMPT_GOVERNANCE_FEATURES,
  DNA_TYPES,
  AUTOMATION_FEATURES,
  FEATURE_MANAGEMENT,
  INTEGRATION_PROVIDERS,
  API_MANAGEMENT_FEATURES,
  SECURITY_FEATURES,
  COMPLIANCE_STANDARDS,
  PLATFORM_OPS_METRICS,
  BILLING_FEATURES,
  AUDIT_EVENTS,
  ANALYTICS_METRICS,
  ACCESSIBILITY_STANDARDS,
  ARCHITECTURE_STAGES,
  LIVE_ROUTES,
  DEMO_KPIS,
  DEMO_ORGS,
  DEMO_PERMISSIONS,
  DEMO_AI,
  DEMO_PROMPTS,
  DEMO_INTEGRATIONS,
  DEMO_SECURITY,
  DEMO_OPS,
  buildPlatformAdminDashboard,
} from '../../platformadmin/framework';
import {
  EnterpriseCommandCenterPanel,
  OrganizationManagementPanel,
  PermissionEnginePanel,
  AIGovernancePanel,
  PromptGovernancePanel,
  IntegrationCenterPanel,
  SecurityCompliancePanel,
  PlatformOperationsPanel,
} from '../../components/platformadmin';
import { Card, KpiCard } from '../../components/ui';

export function PlatformAdministrationPage() {
  const dashboard = buildPlatformAdminDashboard();

  return (
    <div className="space-y-8">
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-brand-600">{UXP_PA_ID}</p>
        <h1 className="text-2xl font-semibold text-slate-900 dark:text-slate-100">Platform Administration</h1>
        <p className="mt-1 text-sm text-slate-500">
          Enterprise Administration, AI Governance, Tenant Management & Platform Operating System —
          the centralized control plane for organizations, AI, security, compliance, and platform operations.
        </p>
        <div className="mt-4 flex flex-wrap gap-2">
          {Object.entries(LIVE_ROUTES).map(([key, path]) => (
            <Link
              key={key}
              to={path}
              className="rounded-lg border border-orange-200 bg-orange-50 px-3 py-1.5 text-xs font-medium text-orange-700 hover:bg-orange-100 dark:border-orange-900 dark:bg-orange-950 dark:text-orange-300"
            >
              {path}
            </Link>
          ))}
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard label="Command Center Metrics" value={dashboard.command_center_metrics.toString()} />
        <KpiCard label="Org Features" value={dashboard.org_features.toString()} />
        <KpiCard label="Permission Scopes" value={dashboard.permission_scopes.toString()} />
        <KpiCard label="Integration Providers" value={dashboard.integration_providers.toString()} />
      </div>

      <section>
        <h2 className="text-lg font-semibold">Architecture Pipeline</h2>
        <div className="mt-3 flex flex-wrap gap-2">
          {ARCHITECTURE_STAGES.map((s, i) => (
            <span key={s} className="flex items-center gap-1 text-xs">
              <span className="rounded-lg border border-slate-200 px-2 py-1 dark:border-slate-700">{s}</span>
              {i < ARCHITECTURE_STAGES.length - 1 && <span className="text-slate-400">→</span>}
            </span>
          ))}
        </div>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Enterprise Command Center</h2>
        <p className="mt-1 text-sm text-slate-500">{COMMAND_CENTER_METRICS.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <EnterpriseCommandCenterPanel kpis={DEMO_KPIS} />
        </Card>
      </section>

      <section className="grid gap-6 lg:grid-cols-2">
        <div>
          <h2 className="text-lg font-semibold">Organization Management</h2>
          <p className="mt-1 text-sm text-slate-500">{ORG_MANAGEMENT_FEATURES.join(' · ')}</p>
          <Card className="mt-4" padding="sm">
            <OrganizationManagementPanel organizations={DEMO_ORGS} />
          </Card>
        </div>
        <div>
          <h2 className="text-lg font-semibold">User & Role Management</h2>
          <p className="mt-1 text-sm text-slate-500">{USER_ROLE_FEATURES.join(' · ')}</p>
          <Card className="mt-4" padding="sm">
            <div className="space-y-2 text-sm">
              <p className="font-medium">142 active users · 18 pending invitations</p>
              <p className="text-slate-600 dark:text-slate-400">12 role templates · 6 delegated access grants</p>
            </div>
          </Card>
        </div>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Permission Engine</h2>
        <p className="mt-1 text-sm text-slate-500">{PERMISSION_SCOPES.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <PermissionEnginePanel permissions={DEMO_PERMISSIONS} />
        </Card>
      </section>

      <section className="grid gap-6 lg:grid-cols-2">
        <div>
          <h2 className="text-lg font-semibold">AI Governance</h2>
          <p className="mt-1 text-sm text-slate-500">{AI_GOVERNANCE_FEATURES.join(' · ')}</p>
          <Card className="mt-4" padding="sm">
            <AIGovernancePanel providers={DEMO_AI} />
          </Card>
        </div>
        <div>
          <h2 className="text-lg font-semibold">Prompt Governance</h2>
          <p className="mt-1 text-sm text-slate-500">{PROMPT_GOVERNANCE_FEATURES.join(' · ')}</p>
          <Card className="mt-4" padding="sm">
            <PromptGovernancePanel prompts={DEMO_PROMPTS} />
          </Card>
        </div>
      </section>

      <section>
        <h2 className="text-lg font-semibold">DNA Governance</h2>
        <p className="mt-1 text-sm text-slate-500">{DNA_TYPES.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <div className="flex flex-wrap gap-2">
            {DNA_TYPES.map((d) => (
              <span key={d} className="rounded border border-slate-200 px-2 py-1 text-xs dark:border-slate-700">{d}</span>
            ))}
          </div>
        </Card>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Automation Engine</h2>
        <p className="mt-1 text-sm text-slate-500">{AUTOMATION_FEATURES.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <div className="space-y-2 text-sm">
            <p className="font-medium">24 active workflows · 8 AI agents · 142 scheduled jobs</p>
            <p className="text-slate-600 dark:text-slate-400">3 escalations pending · 98.2% automation success rate</p>
          </div>
        </Card>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Integration Center</h2>
        <p className="mt-1 text-sm text-slate-500">{INTEGRATION_PROVIDERS.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <IntegrationCenterPanel integrations={DEMO_INTEGRATIONS} />
        </Card>
      </section>

      <section className="grid gap-6 lg:grid-cols-2">
        <div>
          <h2 className="text-lg font-semibold">Security & Compliance</h2>
          <p className="mt-1 text-sm text-slate-500">{SECURITY_FEATURES.join(' · ')} · {COMPLIANCE_STANDARDS.join(' · ')}</p>
          <Card className="mt-4" padding="sm">
            <SecurityCompliancePanel items={DEMO_SECURITY} />
          </Card>
        </div>
        <div>
          <h2 className="text-lg font-semibold">API Management</h2>
          <p className="mt-1 text-sm text-slate-500">{API_MANAGEMENT_FEATURES.join(' · ')}</p>
          <Card className="mt-4" padding="sm">
            <div className="space-y-2 text-sm">
              <p className="font-medium">48 API keys active · 2.4M requests/day</p>
              <p className="text-slate-600 dark:text-slate-400">Rate limit: 1000 req/min · 0 violations today</p>
            </div>
          </Card>
        </div>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Platform Operations</h2>
        <p className="mt-1 text-sm text-slate-500">{PLATFORM_OPS_METRICS.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <PlatformOperationsPanel metrics={DEMO_OPS} />
        </Card>
      </section>

      <section className="grid gap-6 lg:grid-cols-2">
        <div>
          <h2 className="text-lg font-semibold">Credit & Billing</h2>
          <p className="mt-1 text-sm text-slate-500">{BILLING_FEATURES.join(' · ')}</p>
          <Card className="mt-4" padding="sm">
            <div className="space-y-2 text-sm">
              <p className="font-medium">MRR: ₹42.8L · ARR: ₹5.1Cr · Churn: 2.1%</p>
              <p className="text-slate-600 dark:text-slate-400">AI costs: ₹1.9L/mo · Credits consumed: 68%</p>
            </div>
          </Card>
        </div>
        <div>
          <h2 className="text-lg font-semibold">Feature Management</h2>
          <p className="mt-1 text-sm text-slate-500">{FEATURE_MANAGEMENT.join(' · ')}</p>
          <Card className="mt-4" padding="sm">
            <div className="space-y-2 text-sm">
              <p className="font-medium">42 feature flags · 6 beta features · 3 active A/B tests</p>
            </div>
          </Card>
        </div>
      </section>

      <section className="grid gap-6 lg:grid-cols-3">
        <div>
          <h2 className="text-lg font-semibold">Audit</h2>
          <p className="mt-2 text-xs text-slate-500">{AUDIT_EVENTS.join(' · ')}</p>
        </div>
        <div>
          <h2 className="text-lg font-semibold">Analytics</h2>
          <p className="mt-2 text-xs text-slate-500">{ANALYTICS_METRICS.join(' · ')}</p>
        </div>
        <div>
          <h2 className="text-lg font-semibold">Accessibility</h2>
          <div className="mt-2 flex flex-wrap gap-1">
            {ACCESSIBILITY_STANDARDS.map((s) => (
              <span key={s} className="rounded border border-slate-200 px-2 py-0.5 text-xs dark:border-slate-700">{s}</span>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
